import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import {
  updateIsCreatingLessonCompletion,
  updateIsFetchingOtherCourses,
  updateIsFetchingEnrolledCourses,
  updateIsFetchingTutorials,
  updateIsLoadingCourseDetails,
  updateIsCreatingCourseEnrolment,
  updateIsUpdatingCourseCompletion
} from './slice';
import {
  COURSE_PLATFORM,
  COURSE_TAG,
  HTTP_STATS_CODE
} from 'constants/general';
import {
  captureException,
  capturePostHogEvent
} from 'services/posthog.service';
import { RootState } from 'store';
import { Course, LessonCompletionPayload } from 'interfaces';
import { Tutorial } from 'interfaces/common.interface';
import { POST_HOG_EVENT } from 'constants/events';
import { setHasDataLoadFailed } from '../general/slice';
import {
  isApiDataValid,
  isArray,
  isRecord
} from 'utils/api/api-data-validity.util';

export const getCourseDetails = createAsyncThunk(
  'course/getCourseDetails',
  async (course_id: string, { dispatch }) => {
    try {
      dispatch(updateIsLoadingCourseDetails(true));
      const { data } = await focusBearApi.get(
        Endpoints.COURSE_DETAILS(course_id)
      );
      if (!isApiDataValid(data, isRecord)) {
        dispatch(setHasDataLoadFailed(true));
        return null;
      }
      return data as Course;
    } catch (error) {
      dispatch(updateIsLoadingCourseDetails(false));
      captureException(error);
      return null;
    }
  }
);

export const createLessonCompletion = createAsyncThunk(
  'course/createLessonCompletion',
  async (
    { course_id, lesson_id }: LessonCompletionPayload,
    { dispatch }
  ): Promise<boolean> => {
    dispatch(updateIsCreatingLessonCompletion(true));

    try {
      const { status } = await focusBearApi.post(Endpoints.LESSON_COMPLETE, {
        course_id,
        lesson_id
      });

      if (status !== HTTP_STATS_CODE.CREATED) {
        return false;
      }

      capturePostHogEvent(POST_HOG_EVENT.COMPLETE_COURSE_LESSON, {
        course_id,
        lesson_id
      });

      await Promise.allSettled([
        dispatch(getEnrolledCourses()),
        dispatch(getCourseDetails(course_id))
      ]);

      return true;
    } catch (error) {
      toast.error(t('course.could_not_update_user_progress'));
      captureException(error);

      return false;
    } finally {
      dispatch(updateIsCreatingLessonCompletion(false));
    }
  }
);

export const getTutorials = createAsyncThunk(
  'course/get_tutorials',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsFetchingTutorials(true));
      const { data } = await focusBearApi.get(Endpoints.USER_TUTORIALS);
      if (!isApiDataValid(data, isArray)) {
        dispatch(setHasDataLoadFailed(true));
        return [];
      }
      return data as Tutorial[];
    } catch (error) {
      dispatch(updateIsFetchingTutorials(false));
      captureException(error);
      return [];
    }
  }
);

export const getEnrolledCourses = createAsyncThunk(
  'course/get_enrolled_courses',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsFetchingEnrolledCourses(true));
      const { data } = await focusBearApi.get(Endpoints.ENROLLED_COURSES);
      if (!isApiDataValid(data, isArray)) {
        dispatch(setHasDataLoadFailed(true));
        return [];
      }
      return data as Course[];
    } catch (error) {
      dispatch(updateIsFetchingEnrolledCourses(false));
      captureException(error);
      return [];
    }
  }
);

export const getOtherCourses = createAsyncThunk(
  'course/get_other_courses',
  async (_, { dispatch }) => {
    try {
      dispatch(updateIsFetchingOtherCourses(true));
      const { data } = await focusBearApi.get(
        Endpoints.USER_NOT_ENROLLED_COURSES
      );
      return data ?? [];
    } catch (error) {
      dispatch(updateIsFetchingOtherCourses(false));
      captureException(error);
    }
  }
);

const findMobileCourseId = (otherCourses: Course[]): string | null => {
  const mobileCourse = otherCourses.find(
    (course) =>
      course.is_mobile_course === true ||
      (course.tags ?? []).includes(COURSE_TAG.MOBILE) ||
      course.platform === COURSE_PLATFORM.MOBILE
  );

  return mobileCourse?.id ?? null;
};

const MOBILE_USER_AGENT_REGEX = /iPhone|iPad|Android/i;

export const createCourseEnrolment = createAsyncThunk(
  'course/create_course_enrolment',
  async (course_id: string, { dispatch, getState }): Promise<string | null> => {
    try {
      dispatch(updateIsCreatingCourseEnrolment(true));

      const isMobile =
        typeof navigator !== 'undefined' &&
        MOBILE_USER_AGENT_REGEX.test(navigator.userAgent);

      const state = getState() as RootState;
      const otherCourses = state.course.other_courses ?? [];

      const mobileCourseId = isMobile ? findMobileCourseId(otherCourses) : null;

      const chosenCourseId = mobileCourseId ?? course_id;

      const { status } = await focusBearApi.post(Endpoints.ENROLLED_COURSES, {
        course_id: chosenCourseId
      });

      if (status !== HTTP_STATS_CODE.CREATED) {
        toast.error(t('course.couldnt_process_the_request'));
        return null;
      }

      capturePostHogEvent(POST_HOG_EVENT.ENROL_IN_COURSE, {
        course_id: chosenCourseId
      });

      await Promise.allSettled([
        dispatch(getOtherCourses()),
        dispatch(getEnrolledCourses())
      ]);

      return chosenCourseId;
    } catch (error) {
      toast.error(t('course.couldnt_process_the_request'));
      captureException(error);

      return null;
    } finally {
      dispatch(updateIsCreatingCourseEnrolment(false));
    }
  }
);

export const updateCourseCompletion = createAsyncThunk(
  'course/update_course_completion',
  async (
    {
      course_id,
      isCourseCompleted,
      hasExistingRating,
      rating,
      feedback
    }: {
      course_id: string;
      isCourseCompleted?: boolean;
      hasExistingRating?: boolean;
      rating?: number;
      feedback?: string;
    },
    { dispatch }
  ): Promise<boolean> => {
    const trimmedFeedback = (feedback ?? '').trim();
    const hasRating = (rating ?? 0) > 0;

    try {
      dispatch(updateIsUpdatingCourseCompletion(true));
      if (!isCourseCompleted) {
        const { status } = await focusBearApi.patch(
          Endpoints.ENROLLED_COURSES,
          {
            course_id,
            finished: true
          }
        );
        if (status !== HTTP_STATS_CODE.SUCCESS) {
          toast.error(t('course.couldnt_process_the_request'));
          return false;
        }

        capturePostHogEvent(POST_HOG_EVENT.COMPLETE_ENTIRE_COURSE, {
          course_id
        });
      }

      let ratingPersisted = false;
      if (hasRating && !hasExistingRating) {
        try {
          await focusBearApi.post(Endpoints.COURSE_RATING, {
            course_id,
            rating: rating ?? 0,
            review: trimmedFeedback || undefined
          });
          ratingPersisted = true;
        } catch (error) {
          captureException(error);
        }
      }

      if (hasRating || trimmedFeedback) {
        capturePostHogEvent(POST_HOG_EVENT.COURSE_COMPLETED_FEEDBACK, {
          course_id,
          ...(hasRating ? { rating } : {}),
          feedback: trimmedFeedback,
          rating_persisted: ratingPersisted,
          text_feedback_without_rating: !hasRating && Boolean(trimmedFeedback)
        });
      }

      await Promise.allSettled([
        dispatch(getCourseDetails(course_id)),
        dispatch(getEnrolledCourses())
      ]);

      return true;
    } catch (error) {
      toast.error(t('course.couldnt_process_the_request'));
      captureException(error);
      return false;
    } finally {
      dispatch(updateIsUpdatingCourseCompletion(false));
    }
  }
);
